import { useEffect } from 'react';
import './EarthPowerAnimation.css';

/**
 * PROFESSIONAL EARTH POWER ANIMATION
 * Ground-type special move - erupts the ground beneath the opponent
 * 
 * Key Features:
 * - Ground-type special attack (90 base power, 100% accuracy)
 * - 10% chance to lower Sp. Def
 * - Ground eruption from below with seismic energy
 * - Professional gaming quality with earth fissure and magma effects
 */
export default function EarthPowerAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const opponentRect = opponentEl.getBoundingClientRect();
    const playerRect = playerEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const elements = [];

    // PHASE 1: Seismic Energy Charge (0-500ms)
    playerEl.classList.add('earth-power-user-charge');

    const seismicAura = document.createElement('div');
    seismicAura.className = 'earth-power-seismic-aura';
    seismicAura.style.left = `${playerX - 70}px`;
    seismicAura.style.top = `${playerY - 70}px`;
    container.appendChild(seismicAura);
    elements.push(seismicAura);

    // Ground cracks traveling toward opponent
    for (let i = 0; i < 8; i++) {
      const crackTimer = setTimeout(() => {
        const crack = document.createElement('div');
        crack.className = 'earth-power-crack';
        const dx = opponentX - playerX;
        const dy = opponentY - playerY;
        const progress = (i + 1) / 9;
        crack.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 30 - 15}px`;
        crack.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 15 - 3}px`;
        crack.style.width = `${30 + Math.random() * 20}px`;
        container.appendChild(crack);
        elements.push(crack);
      }, 200 + i * 50);
      elements.push({ timer: crackTimer, type: 'timer' });
    }

    // PHASE 2: Ground Eruption (500-1100ms)
    const phase2Timer = setTimeout(() => {
      // Earth pillars erupting beneath opponent
      for (let i = 0; i < 5; i++) {
        const pillarTimer = setTimeout(() => {
          const pillar = document.createElement('div');
          pillar.className = 'earth-power-pillar';
          pillar.style.left = `${opponentX + (i - 2) * 25 - 12}px`;
          pillar.style.top = `${opponentY + 10}px`;
          container.appendChild(pillar);
          elements.push(pillar);
        }, i * 60);
        elements.push({ timer: pillarTimer, type: 'timer' });
      }

      // Magma glow beneath
      const magmaGlow = document.createElement('div');
      magmaGlow.className = 'earth-power-magma-glow';
      magmaGlow.style.left = `${opponentX - 80}px`;
      magmaGlow.style.top = `${opponentY + 15}px`;
      container.appendChild(magmaGlow);
      elements.push(magmaGlow);

      // Rising dust and debris
      for (let i = 0; i < 14; i++) {
        const debris = document.createElement('div');
        debris.className = 'earth-power-debris';
        debris.style.left = `${opponentX + (Math.random() - 0.5) * 100 - 4}px`;
        debris.style.top = `${opponentY + 10 - 4}px`;
        debris.style.animationDelay = `${i * 30}ms`;
        container.appendChild(debris);
        elements.push(debris);
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Seismic Impact (1100-1600ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('earth-power-opponent-hit');

      // Eruption explosion
      const explosion = document.createElement('div');
      explosion.className = 'earth-power-explosion';
      explosion.style.left = `${opponentX - 110}px`;
      explosion.style.top = `${opponentY - 110}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      // Rock burst particles
      for (let i = 0; i < 18; i++) {
        const rockAngle = (i / 18) * 360;
        const rockDist = 50 + Math.random() * 50;
        const rock = document.createElement('div');
        rock.className = 'earth-power-rock-burst';
        rock.style.left = `${opponentX - 5}px`;
        rock.style.top = `${opponentY - 5}px`;
        rock.style.setProperty('--scatter-x', `${Math.cos(rockAngle * Math.PI / 180) * rockDist}px`);
        rock.style.setProperty('--scatter-y', `${Math.sin(rockAngle * Math.PI / 180) * rockDist - 20}px`);
        rock.style.animationDelay = `${i * 15}ms`;
        container.appendChild(rock);
        elements.push(rock);
      }

      // Seismic shockwaves
      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'earth-power-shockwave';
          sw.style.left = `${opponentX - 70}px`;
          sw.style.top = `${opponentY - 70}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }
    }, 1100);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Settle (1600-1900ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('earth-power-user-charge');

      for (let i = 0; i < 6; i++) {
        const dust = document.createElement('div');
        dust.className = 'earth-power-dust';
        dust.style.left = `${opponentX + (Math.random() - 0.5) * 100 - 8}px`;
        dust.style.top = `${opponentY + (Math.random() - 0.5) * 40 - 8}px`;
        dust.style.animationDelay = `${i * 40}ms`;
        container.appendChild(dust);
        elements.push(dust);
      }
    }, 1600);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1900ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('earth-power-user-charge');
      opponentEl.classList.remove('earth-power-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1900);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('earth-power-user-charge');
      opponentEl.classList.remove('earth-power-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

EarthPowerAnimation.moveType = 'ground';
